import { Typography } from '@material-ui/core';

import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';

import {
  SimpleMarkdown,
  extractLastNameComponent,
  extractWorkflowFromInstanceName,
} from '@spotify/confidence-core-react';
import { FeedActivityFragment } from '@spotify/confidence-graphql';
import { FullActivityFeedItem } from '@spotify/plugin-confidence-activity-react';
import { useIdentity } from '@spotify/plugin-confidence-iam-react';

import { useGetWorkflowInstance } from '../../hooks/useGetWorkflowInstance';
import { workflowInstanceRouteRef } from '../../routes';

interface ResourceLinkProps {
  resourceRef: string;
  children?: React.ReactNode;
}

const IdentityResource = ({ resourceRef, children }: ResourceLinkProps) => {
  const { identity } = useIdentity({ name: resourceRef });

  return (
    <Typography component="span">
      {identity?.displayName ?? children}
    </Typography>
  );
};

const WorkflowInstanceResource = ({
  resourceRef,
  children,
}: ResourceLinkProps) => {
  const workflowId = extractWorkflowFromInstanceName(resourceRef)!;
  const instanceId = extractLastNameComponent(resourceRef)!;

  const { workflowInstance } = useGetWorkflowInstance({
    workflowId,
    instanceId,
  });

  const route = useRouteRef(workflowInstanceRouteRef);
  const href = route({
    workflow: workflowId,
    id: instanceId,
  });

  return (
    <Typography component={Link} to={href} color="textPrimary">
      <strong>{workflowInstance?.displayName ?? children}</strong>
    </Typography>
  );
};

const ResourceLink = ({ resourceRef, children }: ResourceLinkProps) => {
  if (resourceRef.startsWith('identities/')) {
    return (
      <IdentityResource resourceRef={resourceRef}>{children}</IdentityResource>
    );
  }

  if (resourceRef.startsWith('workflows/')) {
    return (
      <WorkflowInstanceResource resourceRef={resourceRef}>
        {children}
      </WorkflowInstanceResource>
    );
  }

  return (
    <Typography component={Link} to={resourceRef} color="inherit">
      <strong>{children}</strong>
    </Typography>
  );
};

export interface WorkflowInstanceFeedItemProps {
  activity: FeedActivityFragment;
}

export const WorkflowInstanceFeedItem = ({
  activity,
}: WorkflowInstanceFeedItemProps) => {
  const body = (
    <SimpleMarkdown
      variant="body-medium"
      components={{
        link: ({ href, children }) => <Link to={href}>{children}</Link>,
        resource: ({ resourceRef, children }) => (
          <ResourceLink resourceRef={resourceRef}>{children}</ResourceLink>
        ),
      }}
      content={activity.body}
    />
  );

  return (
    <FullActivityFeedItem activity={activity} title={activity.title}>
      {body}
    </FullActivityFeedItem>
  );
};
